import React, { useContext, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { LocalAdminDTO } from '../../../dto/LocalDTO'
import { TenantsContext } from '../../../context/tenants.context'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { enviarNotifPush } from '../services/admin.local.services'
import SnackBarUtils from '../../../utils/SnackBarUtils'

const MAX_TITULO_LENGTH = 100
const MAX_MSJ_LENGTH = 200

const NotificacionPush: React.FC<{ premise: LocalAdminDTO }> = ({ premise }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [loading, setLoading] = useState<boolean>(false)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [titulo, setTitulo] = useState<string>(null)
  const [mensaje, setMensaje] = useState<string>(null)
  const { tenant } = useContext(TenantsContext)

  const schema = yup.object({
    titulo: yup
      .string()
      .trim()
      .required('Debe ingresar un título')
      .max(MAX_TITULO_LENGTH, `Máximo ${MAX_TITULO_LENGTH} caracteres`),
    msj: yup
      .string()
      .trim()
      .required('El mensaje es obligatorio')
      .max(MAX_MSJ_LENGTH, `Máximo ${MAX_MSJ_LENGTH} caracteres`)
  })

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      titulo: '',
      msj: ''
    }
  })

  const handleClose = () => {
    setTitulo(null)
    setMensaje(null)
    setOpenDialog(false)
    reset()
  }

  const onSubmit = async () => {
    if (!premise?.codLocal) return
    try {
      await enviarNotifPush(tenant, premise.codLocal, { titulo, mensaje })
      SnackBarUtils.success('Notificación enviada!')
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
      handleClose()
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex'
        }}
      >
        <button
          style={{ margin: '1rem' }}
          className="btn btn-primary admin-local-btn premise-info-child"
          onClick={() => setOpenDialog(true)}
        >
          {'Enviar notif. push al local'}
        </button>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleClose}
        PaperProps={{
          sx: {
            display: 'flex',
            width: isMobile ? '90%' : { xs: '90vw', sm: 550 },
            maxWidth: isMobile ? '90%' : '90vw',
            marginLeft: '-20px'
          }
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ overflowX: 'hidden' }}>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            Notificación
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Stack sx={{ width: '100%' }} spacing={1}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Título
                  <Typography variant="subtitle2">
                    {titulo?.length || 0}/{MAX_TITULO_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  {...register('titulo')}
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  variant="outlined"
                  disabled={loading}
                  sx={{ width: '100%' }}
                  inputProps={{ maxLength: MAX_TITULO_LENGTH }}
                  error={!!errors.titulo}
                  helperText={errors?.titulo && String(errors.titulo.message)}
                />
              </Stack>
              <Stack sx={{ width: '100%', margin: '10px 0' }} spacing={1}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Mensaje
                  <Typography variant="subtitle2">
                    {mensaje?.length || 0}/{MAX_MSJ_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  {...register('msj')}
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  variant="outlined"
                  disabled={loading}
                  sx={{ width: '100%' }}
                  inputProps={{ maxLength: MAX_MSJ_LENGTH }}
                  error={!!errors.msj}
                  helperText={errors?.msj && String(errors.msj.message)}
                />
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between'
                }}
              >
                <Button
                  disabled={loading}
                  sx={{ width: '48%' }}
                  variant="contained"
                  color="error"
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button variant="contained" type="submit" disabled={loading} sx={{ width: '48%' }} color="success">
                  Enviar
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </form>
      </Dialog>
    </>
  )
}

export default NotificacionPush
